import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Copy, Sparkles, ShieldOff, CheckCircle2 } from "lucide-react";
import { Card, Btn, Input, Field } from "@/components/admin/ui";
import { API } from "@/config";

/* ==================== Admin: Promo Codes ====================
 * Firefly Wishes codes are still auto-generated from post-stay review
 * emails. Admin can ALSO hand-create codes here — for marketing lists,
 * influencer partnerships, refund credits, etc.
 */
export const PromoCodes = () => {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState("all");

  // New-code form
  const [code, setCode] = useState("");
  const [pct, setPct] = useState(10);
  const [email, setEmail] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [startsAt, setStartsAt] = useState("");
  const [maxUses, setMaxUses] = useState(0);
  const [notes, setNotes] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/promo-codes`);
      setCodes(data.codes || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't load promo codes.");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e?.preventDefault?.();
    if (!(Number(pct) > 0 && Number(pct) <= 100)) { toast.error("Discount % must be 1–100."); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/promo-codes`, {
        code: code.trim() || undefined,
        discount_percent: Number(pct),
        customer_email: email.trim() || undefined,
        expires_at: expiresAt || undefined,
        starts_at: startsAt || undefined,
        max_uses: Number(maxUses) || 0,
        notes: notes.trim() || undefined,
      });
      toast.success("Promo code created ✨");
      setCode(""); setPct(10); setEmail(""); setExpiresAt(""); setStartsAt(""); setMaxUses(0); setNotes("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Create failed.");
    } finally { setSaving(false); }
  };

  const toggleActive = async (c) => {
    try {
      await axios.patch(`${API}/admin/promo-codes/${c.code}`, { active: !(c.active !== false) });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed."); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete code ${c.code}? This can't be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/promo-codes/${c.code}`);
      toast.success("Deleted.");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed."); }
  };

  const copy = (c) => {
    navigator.clipboard?.writeText(c.code);
    toast.success(`Copied ${c.code}`);
  };

  const filtered = useMemo(() => {
    if (filter === "active") return codes.filter((c) => c.active !== false && (c.remaining_uses === null || c.remaining_uses > 0));
    if (filter === "redeemed") return codes.filter((c) => (c.uses_count || 0) > 0);
    if (filter === "inactive") return codes.filter((c) => c.active === false);
    return codes;
  }, [codes, filter]);

  const now = new Date().toISOString();

  return (
    <div className="space-y-6" data-testid="admin-promo-codes">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-serif text-3xl text-[#FFD166]">Promo Codes</h1>
          <p className="text-sm text-[#B9C8D5]">Create custom discount codes customers can enter on the checkout page.</p>
        </div>
        <div className="flex items-center gap-2 text-xs">
          {["all","active","redeemed","inactive"].map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setFilter(f)}
              className={`px-3 py-1.5 rounded-full border ${filter===f ? "border-[#FFD166] bg-[#FFD166]/10 text-[#FFD166]" : "border-[#B9C8D5]/30 text-[#B9C8D5] hover:border-[#FFD166]/40"}`}
              data-testid={`promo-filter-${f}`}
            >{f}</button>
          ))}
        </div>
      </div>

      <Card>
        <div className="p-5">
          <div className="flex items-center gap-2 mb-3">
            <Plus className="w-4 h-4 text-[#FFD166]" />
            <h2 className="font-serif text-xl text-[#FFD166]">Create a new code</h2>
          </div>
          <form onSubmit={create} className="grid md:grid-cols-[1.2fr_0.6fr_1.3fr_0.7fr_0.9fr_0.9fr_auto] gap-3 items-end" data-testid="promo-create-form">
            <Field label="Code (optional)">
              <Input
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="Auto-generated if empty"
                data-testid="promo-code-input"
              />
            </Field>
            <Field label="Discount %">
              <Input type="number" min="1" max="100" step="1" value={pct} onChange={(e) => setPct(e.target.value)} required data-testid="promo-pct-input" />
            </Field>
            <Field label="Lock to email (optional)">
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Leave blank for a public code" data-testid="promo-email-input" />
            </Field>
            <Field label="Max uses (0 = unlimited)">
              <Input type="number" min="0" step="1" value={maxUses} onChange={(e) => setMaxUses(e.target.value)} data-testid="promo-max-uses-input" />
            </Field>
            <Field label="Starts (optional)">
              <Input type="date" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} data-testid="promo-starts-input" />
            </Field>
            <Field label="Expires (optional)">
              <Input type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} data-testid="promo-expires-input" />
            </Field>
            <Btn disabled={saving} data-testid="promo-create-btn">
              <Sparkles className="w-3.5 h-3.5" /> {saving ? "Creating…" : "Create"}
            </Btn>
            <div className="md:col-span-7">
              <Field label="Admin notes (optional)">
                <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. Instagram influencer @jane, valid Feb 2026" data-testid="promo-notes-input" />
              </Field>
            </div>
          </form>
          <p className="text-[10px] text-[#B9C8D5]/70 mt-2">
            Codes are single-use. Leave the email blank to let anyone with the code redeem it (first come, first served).
          </p>
        </div>
      </Card>

      <Card>
        <div className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-serif text-xl text-[#FFD166]">All codes ({filtered.length})</h2>
          </div>
          {loading ? (
            <p className="text-[#B9C8D5]">Loading…</p>
          ) : filtered.length === 0 ? (
            <p className="text-[#B9C8D5]/60 italic text-sm">No codes match this filter.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="promo-code-table">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-widest text-[#B9C8D5] border-b border-[#FFD166]/15">
                    <th className="py-2 pr-3">Code</th>
                    <th className="py-2 pr-3">Off</th>
                    <th className="py-2 pr-3">Locked to</th>
                    <th className="py-2 pr-3">Source</th>
                    <th className="py-2 pr-3">Uses</th>
                    <th className="py-2 pr-3">Status</th>
                    <th className="py-2 pr-3">Starts</th>
                    <th className="py-2 pr-3">Expires</th>
                    <th className="py-2 pr-3">Notes</th>
                    <th className="py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((c) => {
                    const used = c.uses_count || 0;
                    const exhausted = c.remaining_uses !== null && c.remaining_uses !== undefined && c.remaining_uses <= 0;
                    const redeemed = exhausted;
                    const expired = c.expires_at && c.expires_at < now;
                    const inactive = c.active === false;
                    return (
                      <tr key={c.code} className="border-b border-[#FFD166]/5 hover:bg-black/25" data-testid={`promo-row-${c.code}`}>
                        <td className="py-2 pr-3 font-mono text-[#FFD166]">
                          <button type="button" onClick={() => copy(c)} className="hover:underline inline-flex items-center gap-1.5" data-testid={`promo-copy-${c.code}`}>
                            {c.code} <Copy className="w-3 h-3 opacity-60" />
                          </button>
                        </td>
                        <td className="py-2 pr-3">{Number(c.discount_percent || 0).toFixed(0)}%</td>
                        <td className="py-2 pr-3 text-[#B9C8D5]">{c.customer_email || <span className="text-[#7EE787]">public</span>}</td>
                        <td className="py-2 pr-3 text-[#B9C8D5] capitalize">{c.source || "firefly"}</td>
                        <td className="py-2 pr-3 text-[#B9C8D5] whitespace-nowrap" data-testid={`promo-uses-${c.code}`}>
                          {used} / {c.max_uses ? c.max_uses : "∞"}
                          <span className="block text-[11px] opacity-70">{c.max_uses ? `${c.remaining_uses} left` : "Unlimited"}</span>
                        </td>
                        <td className="py-2 pr-3">
                          {redeemed ? (
                            <span className="inline-flex items-center gap-1 text-[#B9C8D5]"><CheckCircle2 className="w-3.5 h-3.5" /> {c.max_uses === 1 ? "Redeemed" : "Max reached"}</span>
                          ) : expired ? (
                            <span className="text-red-300">Expired</span>
                          ) : inactive ? (
                            <span className="text-[#F4A93A]">Inactive</span>
                          ) : (
                            <span className="text-[#7EE787]">Active</span>
                          )}
                        </td>
                        <td className="py-2 pr-3 text-[#B9C8D5]">{c.starts_at ? c.starts_at.slice(0, 10) : "—"}</td>
                        <td className="py-2 pr-3 text-[#B9C8D5]">{c.expires_at ? c.expires_at.slice(0, 10) : "—"}</td>
                        <td className="py-2 pr-3 text-[#B9C8D5] max-w-[280px] truncate" title={c.notes || ""}>{c.notes || "—"}</td>
                        <td className="py-2 flex items-center gap-2">
                          <button
                            type="button"
                            onClick={async () => { const v = window.prompt(`Maximum uses for ${c.code} (0 = unlimited)`, String(c.max_uses ?? 0)); if (v === null) return; try { await axios.patch(`${API}/admin/promo-codes/${c.code}`, { max_uses: Number(v) || 0 }); load(); } catch (err) { toast.error(err.response?.data?.detail || "Update failed."); } }}
                            className="text-xs text-[#B9C8D5] hover:text-[#FFD166]"
                            data-testid={`promo-edit-max-${c.code}`}
                          >Edit max</button>
                          {(
                            <button
                              type="button"
                              onClick={() => toggleActive(c)}
                              className="text-xs text-[#B9C8D5] hover:text-[#FFD166] inline-flex items-center gap-1"
                              data-testid={`promo-toggle-${c.code}`}
                            >
                              <ShieldOff className="w-3.5 h-3.5" /> {inactive ? "Reactivate" : "Deactivate"}
                            </button>
                          )}
                          {used === 0 && (
                            <button
                              type="button"
                              onClick={() => remove(c)}
                              className="text-xs text-red-300 hover:text-red-200 inline-flex items-center gap-1"
                              data-testid={`promo-delete-${c.code}`}
                            >
                              <Trash2 className="w-3.5 h-3.5" /> Delete
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default PromoCodes;
